import { useEffect } from 'react';

function isFocusableInput(el) {
  if (!el) return false;
  if (el.disabled) return false;
  if (el.getAttribute?.('aria-hidden') === 'true') return false;
  if (typeof el.focus !== 'function') return false;
  const tag = (el.tagName || '').toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select';
}

function placeCaret(el, { select = false } = {}) {
  if (!el) return;
  if (select && typeof el.select === 'function') {
    el.select();
    return;
  }
  // Put caret at end (better for “blinking cursor visible” than full selection).
  try {
    if (typeof el.setSelectionRange === 'function') {
      const len = (el.value || '').length;
      el.setSelectionRange(len, len);
    }
  } catch {
    // ignore
  }
}

/**
 * Robustly focuses an input after mount / route transitions.
 * - Uses rAF + short timeout to handle transitions/animations.
 * - Does not steal focus if user already focused another input.
 * - Adds a user-gesture fallback (touch/pointer) for mobile keyboards.
 */
export function useAutoFocus(inputRef, { select = false, delayMs = 120 } = {}) {
  useEffect(() => {
    let mounted = true;
    let t;
    let raf1;
    let raf2;
    let removed = false;

    const tryFocus = () => {
      if (!mounted) return;
      const el = inputRef?.current;
      if (!isFocusableInput(el)) return;

      const active = document.activeElement;
      if (active && active !== document.body && active !== document.documentElement && active !== el) {
        // Don't steal focus from another active control.
        return;
      }

      try {
        // preventScroll supported in modern browsers; fallback is fine.
        el.focus({ preventScroll: true });
        placeCaret(el, { select });
      } catch {
        // ignore
      }
    };

    // rAF → rAF → timeout: works better across mobile + animated layouts.
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        tryFocus();
        t = setTimeout(tryFocus, delayMs);
      });
    });

    // Mobile (notably iOS Safari) may ignore programmatic focus for keyboard.
    // This one-time listener ensures focus occurs within a user gesture.
    const onFirstUserGesture = () => {
      if (removed) return;
      tryFocus();
      removed = true;
      window.removeEventListener('pointerdown', onFirstUserGesture, true);
      window.removeEventListener('touchstart', onFirstUserGesture, true);
      window.removeEventListener('mousedown', onFirstUserGesture, true);
    };
    window.addEventListener('pointerdown', onFirstUserGesture, { capture: true, passive: true });
    window.addEventListener('touchstart', onFirstUserGesture, { capture: true, passive: true });
    window.addEventListener('mousedown', onFirstUserGesture, { capture: true, passive: true });

    return () => {
      mounted = false;
      if (raf1) cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
      if (t) clearTimeout(t);
      if (!removed) {
        window.removeEventListener('pointerdown', onFirstUserGesture, true);
        window.removeEventListener('touchstart', onFirstUserGesture, true);
        window.removeEventListener('mousedown', onFirstUserGesture, true);
      }
    };
  }, [inputRef, select, delayMs]);
}

